import { useQuery } from "@tanstack/react-query";
import { queueApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type { PartyInviteResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

const PARTY_INVITES_FOREGROUND_REFRESH_MS = 15_000;

/**
 * The caller's incoming PENDING party invites (GET /queue/party-invites/mine).
 *
 * Returned VERBATIM as PartyInviteResponse objects, newest-first (the backend
 * orders + scopes the list to the token user; cache identity additionally carries
 * the authenticated UID so pending reads cannot cross an account switch).
 * `facility_id`, `party_code`, and `party_member_count` are projected
 * server-side from the parent party — surfaces render and filter them but never
 * derive them. A respond mutation invalidates the party arm plus the affected
 * facility's current-party inventory.
 */
export function useMyPartyInvites(enabled: boolean = true) {
  const { user } = useAuth();
  return useQuery<PartyInviteResponse[]>({
    queryKey: [...queryKeys.party.invitesMine(), user?.uid],
    queryFn: () => queueApi.listMyPartyInvites(),
    enabled: enabled && Boolean(user),
    refetchInterval: PARTY_INVITES_FOREGROUND_REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnMount: "always",
  });
}
